import { useRef, useState } from 'react'
import { X, Gift, Loader2, ScanLine } from 'lucide-react'
import { useQueryClient } from '@tanstack/react-query'
import { useSellGiftCard, useCreateReceipt, useAddReceiptItem, receiptKeys, type Receipt } from '@/api/receipts'
import { useCheckGiftCardBalance, extractGiftCardBalance, isBirchmountCard } from '@/api/payments'
import { usePOSStore } from '@/stores/posStore'
import { useUIStore } from '@/stores/uiStore'
import { useBarcodeScanner } from '@/hooks'
import { formatPrice } from '@/lib/utils'
import { classifyBalanceCheckError, type BalanceCheckError } from '@/lib/giftCardCheck.core'

// v1 had this guard server-side (POSGiftCardsApiHandler with receipt_id);
// v2 dropped it. We re-add it client-side: the cashier shouldn't be able
// to sell the same card number twice on a single receipt. Reading from
// react-query's receipt cache — the receipt is repopulated on every
// successful /items POST, so this is always live.
function isCardOnReceipt(
  receipt: Receipt | undefined,
  cardNumber: string,
): boolean {
  if (!receipt?.items || !cardNumber) return false
  const target = cardNumber.trim()
  return receipt.items.some(
    (it) => (it as { gift_card_number?: string | null }).gift_card_number === target,
  )
}

export function GiftCardSellModal() {
  const { closeModal, showToast } = useUIStore()
  const { cart, currentReceiptId, selectedCustomer, setReceiptId, addGiftCardLine } = usePOSStore()
  const queryClient = useQueryClient()

  const [cardNumber, setCardNumber] = useState('')
  const [balance, setBalance] = useState('')
  const [currentBalance, setCurrentBalance] = useState<number | null>(null)
  const [isBirchmount, setIsBirchmount] = useState(false)
  const [duplicateOnReceipt, setDuplicateOnReceipt] = useState(false)
  // Birchmount does not hold this number, so the card cannot be sold at all.
  // Distinct from the new-card path below, which is legitimate at a store
  // that runs our own Barnet cards.
  const [notAtProvider, setNotAtProvider] = useState(false)
  const [error, setError] = useState('')

  const sellGiftCard = useSellGiftCard()
  const createReceipt = useCreateReceipt()
  const addReceiptItem = useAddReceiptItem()
  const checkBalance = useCheckGiftCardBalance()

  const isPending = sellGiftCard.isPending || createReceipt.isPending || addReceiptItem.isPending
  const balanceNum = parseFloat(balance) || 0

  const ensureReceiptExists = async (): Promise<number> => {
    if (currentReceiptId) return currentReceiptId
    const receipt = await createReceipt.mutateAsync({ customer_id: selectedCustomer?.id })
    setReceiptId(receipt.id)
    for (const item of cart) {
      await addReceiptItem.mutateAsync({
        receiptId: receipt.id,
        item: {
          product_id: item.product_id,
          qty: item.quantity,
          price: item.unit_price,
          lot_number: item.lot_number || undefined,
        },
      })
    }
    return receipt.id
  }

  // Latest-wins token. A Birchmount lookup can run for seconds, so a second
  // scan or an edit to the number can easily land mid-flight. Every path
  // that invalidates the current answer bumps this, and a resolution that
  // no longer owns the token drops itself — otherwise one card's verdict
  // renders under another card's number, which at best blocks a good card
  // and at worst offers "New card" for a bogus one.
  const checkSeq = useRef(0)
  const inFlightCard = useRef('')

  // `card` defaults to the input's current value; the scan path passes the
  // scanned code explicitly because setCardNumber hasn't flushed yet.
  const handleCheckBalance = async (card: string = cardNumber) => {
    const trimmed = card.trim()
    if (!trimmed) return
    // Enter calls this directly, so a double-press would fire two identical
    // lookups. The token makes that harmless, but it still spends a
    // Birchmount round-trip while the cashier is waiting on the first.
    //
    // Only ever suppresses a true duplicate: anything that invalidates the
    // in-flight answer (an edit, a re-scan) clears inFlightCard, because
    // otherwise this would swallow the REPLACEMENT check and leave the
    // cashier with a blank panel — the in-flight answer already discarded
    // by the token, and nothing on its way to replace it.
    if (checkBalance.isPending && trimmed === inFlightCard.current) return
    inFlightCard.current = trimmed

    const seq = (checkSeq.current += 1)
    setCurrentBalance(null)
    setDuplicateOnReceipt(false)
    setNotAtProvider(false)
    // Clear the badge here too: re-checking the same number via Enter does
    // not go through the input's onChange, so the previous card's "via
    // Birchmount" badge would otherwise show through the pending state.
    setIsBirchmount(false)
    setError('')

    // Check for duplicate on the current receipt before we even hit the
    // balance API — saves a Birchmount round-trip in the duplicate case.
    if (currentReceiptId) {
      const cached = queryClient.getQueryData<Receipt>(receiptKeys.detail(currentReceiptId))
      if (isCardOnReceipt(cached, trimmed)) {
        setDuplicateOnReceipt(true)
        return
      }
    }

    try {
      const result = await checkBalance.mutateAsync(trimmed)
      if (seq !== checkSeq.current) return
      setCurrentBalance(extractGiftCardBalance(result.card))
      setIsBirchmount(isBirchmountCard(result.card))
    } catch (err: unknown) {
      if (seq !== checkSeq.current) return
      setIsBirchmount(false)
      switch (classifyBalanceCheckError(err as BalanceCheckError)) {
        case 'unavailable':
          setError('Gift card service is temporarily unavailable. Please try again.')
          break
        case 'not-at-provider':
          // Birchmount does not hold this number — a mis-scan or a mistyped
          // digit. Stop the cashier here rather than at the till: the sale
          // would otherwise fail only after the customer had tendered,
          // which on a terminal payment leaves a charge to unwind.
          setNotAtProvider(true)
          break
        case 'new-card':
          // The only path that may proceed. currentBalance going non-null
          // is what handleSell trusts as proof a check actually ran.
          setCurrentBalance(0)
          break
        default:
          // Inconclusive — network, timeout, 500, interstitial, reauth
          // pause. We know nothing about this card, so leave currentBalance
          // null and Add disabled rather than certifying it as checked.
          setError('Could not check this card. Try again.')
      }
    }
  }

  // While this modal is open, a hardware scan means "this gift card" —
  // never a SKU. The route's global scanner is disabled for
  // `gift-card-sell` (one scan path at a time, same as checkout and the
  // camera scanner), so we own the wedge here: the code lands in the card
  // number field no matter which control has focus, and we run the
  // balance check straight away since that's the cashier's next move.
  // Human typing still fails the hook's timing gates and behaves as
  // before — including Enter-to-check from the input itself.
  const handleScan = (code: string) => {
    const scanned = code.trim()
    if (!scanned) return
    setCardNumber(scanned)
    // A scan is always a fresh answer, so release any in-flight lookup's
    // claim — otherwise re-scanning the same card while its check is still
    // running is mistaken for a duplicate and suppressed. handleCheckBalance
    // clears the rest of the state.
    inFlightCard.current = ''
    handleCheckBalance(scanned)
  }

  // Deliberately NOT gated on checkBalance.isPending. Disabling the hook
  // tears down its keydown listener, which does not stop the wedge — it
  // just sends the burst to whatever has focus, appending the second card's
  // digits onto the first in the number field. Keeping the scanner armed
  // means the hook still owns the burst; checkSeq handles the overlap.
  useBarcodeScanner({ onScan: handleScan, enabled: !isPending })

  const handleSell = async () => {
    if (isPending) return
    setError('')

    if (!cardNumber.trim()) { setError('Enter a gift card number.'); return }
    if (balanceNum <= 0) { setError('Enter a valid amount to load.'); return }

    // A `disabled` attribute is not a guard: the amount field's Enter key
    // calls this directly, so both of these must be re-asserted here or the
    // most natural keyboard path walks straight past them.
    if (notAtProvider) {
      setError('That card number is not recognised, so it cannot be sold.')
      return
    }
    if (currentBalance === null) {
      // Never checked, or the number was edited after checking. Selling a
      // card nobody validated is how an unsellable number reaches a receipt
      // and fails at payment, after the customer has tendered.
      setError('Check the card number before adding it to the receipt.')
      return
    }

    // Re-check duplicate at submit time (defense-in-depth — the cashier
    // could change the input after the balance check). Receipt may have
    // been created mid-flow by another action; recheck the live cache.
    const liveReceiptId = currentReceiptId ?? usePOSStore.getState().currentReceiptId
    if (liveReceiptId) {
      const cached = queryClient.getQueryData<Receipt>(receiptKeys.detail(liveReceiptId))
      if (isCardOnReceipt(cached, cardNumber)) {
        setDuplicateOnReceipt(true)
        return
      }
    }

    try {
      const receiptId = await ensureReceiptExists()
      const trimmedCard = cardNumber.trim()
      const receipt = await sellGiftCard.mutateAsync({ receiptId, cardNumber: trimmedCard, balance: balanceNum })
      // Mirror the BE-assigned gift-card line back into the local cart.
      // Receipt.items may carry the v2 shape with gift_card_number; cast
      // to read it without widening the public Receipt type.
      const newGc = receipt.items?.find(
        (it) => (it as { gift_card_number?: string | null }).gift_card_number === trimmedCard,
      ) as ({ id: number; product_id: number; description: string | null; gift_card_number: string | null }) | undefined
      if (newGc) {
        addGiftCardLine(
          {
            id: newGc.id,
            product_id: newGc.product_id,
            description: newGc.description,
            gift_card_number: newGc.gift_card_number,
          },
          balanceNum,
        )
      }
      closeModal()
      showToast({
        type: 'success',
        title: 'Gift Card Added',
        message: `$${formatPrice(balanceNum)} gift card added to receipt.`,
      })
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { error?: string } } }
      setError(axiosErr?.response?.data?.error || (err instanceof Error ? err.message : 'Could not add gift card.'))
    }
  }

  const isNewCard = currentBalance === 0
  const hasChecked = currentBalance !== null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center animate-fade-in">
      <div className="absolute inset-0 bg-black/50" onClick={!isPending ? closeModal : undefined} />
      <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-md overflow-hidden flex flex-col animate-scale-in">
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <div className="flex items-center gap-2">
            <Gift size={20} className="text-purple-500" />
            <h2 className="text-lg font-semibold text-gray-900">Gift Card</h2>
          </div>
          {!isPending && (
            <button onClick={closeModal} className="p-1 hover:bg-gray-100 rounded-lg transition-colors cursor-pointer">
              <X size={20} className="text-gray-500" />
            </button>
          )}
        </div>

        <div className="p-5 space-y-4">
          {/* Card Number */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Gift Card Number</label>
            <div className="flex gap-2">
              <input
                type="text"
                value={cardNumber}
                onChange={(e) => { checkSeq.current += 1; inFlightCard.current = ''; setCardNumber(e.target.value); setCurrentBalance(null); setIsBirchmount(false); setDuplicateOnReceipt(false); setNotAtProvider(false); setError('') }}
                onKeyDown={(e) => e.key === 'Enter' && handleCheckBalance()}
                placeholder="Scan or enter card number"
                disabled={isPending}
                className="flex-1 px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-purple-500 focus:ring-0 outline-none font-mono disabled:bg-gray-50"
                autoFocus
              />
              <button
                onClick={() => handleCheckBalance()}
                disabled={!cardNumber.trim() || checkBalance.isPending || isPending}
                className="px-3 py-3 bg-gray-100 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed rounded-xl transition-colors flex items-center gap-1.5 text-sm"
              >
                {checkBalance.isPending
                  ? <Loader2 size={16} className="animate-spin" />
                  : <ScanLine size={16} />
                }
                Check
              </button>
            </div>
          </div>

          {/* Balance Status */}
          {duplicateOnReceipt ? (
            <div className="p-3 rounded-xl border text-sm bg-amber-50 border-amber-200 text-amber-700">
              This card is already on the current receipt. Use a different card.
            </div>
          ) : notAtProvider ? (
            <div className="p-3 rounded-xl border text-sm bg-red-50 border-red-200 text-red-700">
              <span className="font-mono">{cardNumber}</span> isn&rsquo;t recognised. Re-scan or
              re-key it &mdash; if it still fails, this card can&rsquo;t be sold here.
            </div>
          ) : hasChecked && (
            <div className={`p-3 rounded-xl border text-sm ${isNewCard ? 'bg-blue-50 border-blue-200 text-blue-700' : 'bg-green-50 border-green-200 text-green-700'}`}>
              <div className="flex items-center justify-between gap-2">
                <span>
                  {isNewCard
                    ? 'New card — will be created on checkout.'
                    : `Current balance: $${formatPrice(currentBalance!)}. Loading more funds.`
                  }
                </span>
                {isBirchmount && (
                  <span className="shrink-0 text-[11px] font-semibold uppercase tracking-wide bg-white/60 border border-current px-1.5 py-0.5 rounded">
                    via Birchmount
                  </span>
                )}
              </div>
            </div>
          )}

          {/* Amount */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              {isNewCard ? 'Load Amount' : 'Add Funds'}
            </label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 font-medium">$</span>
              <input
                type="number"
                min="0"
                step="0.01"
                value={balance}
                onChange={(e) => { setBalance(e.target.value); setError('') }}
                onKeyDown={(e) => e.key === 'Enter' && handleSell()}
                placeholder="0.00"
                disabled={isPending}
                className="w-full pl-7 pr-4 py-3 text-xl font-bold border-2 border-gray-200 rounded-xl focus:border-purple-500 focus:ring-0 outline-none disabled:bg-gray-50"
              />
            </div>
          </div>

          {/* Quick amounts */}
          <div className="flex gap-2">
            {[25, 50, 100, 200].map((val) => (
              <button
                key={val}
                onClick={() => setBalance(String(val))}
                disabled={isPending}
                className="flex-1 py-2 bg-gray-100 hover:bg-gray-200 disabled:opacity-50 rounded-lg text-sm font-medium transition-colors"
              >
                ${val}
              </button>
            ))}
          </div>

          {error && (
            <p className="text-sm text-red-500 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{error}</p>
          )}

          <div className="flex gap-3">
            <button
              onClick={closeModal}
              disabled={isPending}
              className="flex-1 py-3 border border-gray-200 hover:bg-gray-50 text-gray-700 font-medium rounded-xl transition-colors cursor-pointer disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={handleSell}
              disabled={!cardNumber.trim() || balanceNum <= 0 || isPending || duplicateOnReceipt || notAtProvider || !hasChecked}
              className="flex-1 py-3 bg-purple-500 hover:bg-purple-600 disabled:bg-gray-300 disabled:cursor-not-allowed text-white font-medium rounded-xl transition-colors cursor-pointer flex items-center justify-center gap-2"
            >
              {isPending && <Loader2 size={16} className="animate-spin" />}
              {isPending
                ? 'Adding...'
                : balanceNum > 0
                  ? `Add $${formatPrice(balanceNum)} to Receipt`
                  : 'Add to Receipt'
              }
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
